import { useState } from 'react';
import { BrainIcon } from 'lucide-react';
import { generateOptionWithAI } from '@/lib/api/ai';
import Modal from '@/components/Modal';
import Button from '@/components/form/Button';
import type { SnapCategory } from '@/types/database.types';

interface GenerateOptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: string;
  description: string;
  categories: SnapCategory[];
  onAccept: (title: string, description: string) => void;
}

interface GenerateOptionResponse {
  output: {
    newOption: string;
    newOptionDescription: string;
  };
}

export default function GenerateOptionModal({
  isOpen,
  onClose,
  question,
  description,
  categories,
  onAccept
}: GenerateOptionModalProps) {
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'emotional' | 'rational' | 'economic' | 'ethical'>('rational');
  const [generatedOption, setGeneratedOption] = useState<GenerateOptionResponse['output'] | null>(null);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const data = await generateOptionWithAI(
        question,
        description,
        categories,
        selectedMode
      );
      setGeneratedOption(data);
    } catch (error) {
      console.error('Error generating option:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (generatedOption) {
      onAccept(generatedOption.newOption, generatedOption.newOptionDescription);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Option with AI"
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select AI Perspective
          </label>
          <div className="grid grid-cols-2 gap-2 p-2">
            {(['emotional', 'rational', 'economic', 'ethical'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setSelectedMode(mode)}
                className={`p-3 text-sm font-medium rounded-lg capitalize transition-colors ${
                  selectedMode === mode
                    ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Choose how the AI should approach generating the option
          </p>
        </div>

        {generatedOption ? (
          <div className="space-y-4">
            <div className="bg-primary-50 p-4 rounded-lg space-y-3">
              <div>
                <div className="text-sm font-medium text-gray-600">Option Title:</div>
                <div className="mt-1 text-gray-900">{generatedOption.newOption}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Description:</div>
                <div className="mt-1 text-gray-900">{generatedOption.newOptionDescription}</div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="ghost"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAccept}
                icon={<BrainIcon className="h-4 w-4" />}
              >
                Use This Option
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button
              onClick={handleGenerate}
              loading={loading}
              icon={<BrainIcon className="h-4 w-4" />}
            >
              Generate Option
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}